/* The eval runner.

   No backend, so the agent's behaviour is synthesized — but synthesized
   DETERMINISTICALLY, from hash(case + config snapshot). That constraint is the
   whole reliability story the user asked for:

     · re-running an unchanged agent reproduces the numbers exactly — nothing
       flickers, nobody loses trust in the tab
     · changing the config reshuffles behaviour, so the compare view has real
       regressions and improvements to show without anyone faking a diff
     · the checks themselves are NOT faked. They run for real against the
       synthesized output and trajectory (checks.js), which is why a failing
       case always fails for a legible, quotable reason

   This is the same technique traceModel.js already uses for stable costs
   (`genCost`, `traceScores`) — and the opposite of TeamsPage.jsx:864-914's
   Math.random(), which is called out as a bug in the teams strategy plan. */

import { hashStr, checksFor, composite, runScore, configSnapshot, snapshotKey } from "@/shared/lib/evals-model";
import { runCheck } from "@/shared/lib/evals-checks";

import type {
  Agent, EvalCase, EvalCheck, EvalCompareRow, EvalResult, EvalRun, EvalSuite,
  EvalToolCall, RunEvalOptions,
} from "@/shared/types";

const pick = <T,>(seed: string, arr: T[]): T => arr[hashStr(seed) % arr.length];
const pct = (seed: string): number => (hashStr(seed) % 1000) / 1000;

/* Hedges an agent falls back on when it is doing badly. Recognisable failure
   modes, so a bad output reads as a bad answer rather than as noise. */
const DEGRADED_OPENERS = [
  "I wasn't able to find enough information to answer that fully.",
  "Based on what I could gather, though I'd recommend verifying this:",
  "I don't have access to that — here's a general answer instead.",
];

const GENERIC = [
  "Here is a summary of what I found, with the key figures and their sources.",
  "I've completed the task and written the result to the workspace.",
  "Three options stand out; the second is the strongest on cost and timeline.",
];

function degrade(text: string, seed: string, needles: (string | undefined)[]): string {
  let out = String(text || "");
  // Strip whatever the case asserts on, so the failure is visible in the diff
  // rather than being a mystery the user has to take on faith.
  for (const n of needles) if (n) out = out.split(new RegExp(n, "ig")).join("…");
  return `${pick(seed, DEGRADED_OPENERS)} ${out}`.trim().slice(0, 600);
}

/** The synthesized trajectory: the grader context minus `expected`, which only
   the case knows. Everything here is always produced, so nothing is optional. */
interface SynthTrace {
  output: string;
  tools: string[];
  toolCalls: EvalToolCall[];
  steps: number;
  ms: number;
  cost: number;
  seed: string;
  quality: number;
}

/* What the agent "did" for one case under one config. */
function synthesize(kase: EvalCase, checks: EvalCheck[], key: string): SynthTrace {
  const seed = `${kase.id}|${key}`;
  const q = pct(seed);
  // Roughly two in five cases come back degraded. A suite where everything
  // passes is the classic dead eval set — it scores 100% forever and tells
  // nobody anything.
  const good = q > 0.42;

  const want: string[] = [];
  const forbidden: string[] = [];
  const argSpec: Record<string, string | undefined> = {};   // tool → the arguments the case says it should be called with
  for (const c of checks) {
    if (c.type === "used_tool" && c.tool) {
      want.push(c.tool);
      if (c.args) argSpec[c.tool] = c.args;
    }
    if (c.type === "tool_order" && c.tools) {
      String(c.tools).split(",").map((s) => s.trim()).filter(Boolean).forEach((t) => want.push(t));
    }
    if (c.type === "never_tool" && c.tool) forbidden.push(c.tool);
  }
  const needles = checks.filter((c) => c.type === "contains" && c.text).map((c) => c.text);

  const base = String(kase.expected || "").trim() || pick(`${seed}|g`, GENERIC);
  const output = good ? base : degrade(base, `${seed}|d`, needles);

  let tools = [...new Set(want)];
  if (!good) {
    // Two believable trajectory failures: gave up before the last tool, or
    // reached for something it was told not to touch.
    tools = tools.slice(0, Math.max(0, tools.length - 1));
    if (forbidden.length && q < 0.12) tools.push(forbidden[0]);
  }

  const budgetCheck = checks.find((c) => c.type === "steps_under" && Number(c.n));
  const budget = budgetCheck ? Number(budgetCheck.n) : 8;
  const steps = good
    ? Math.max(1, Math.round(budget * (0.4 + pct(`${seed}|s`) * 0.5)))
    : budget + 1 + (hashStr(`${seed}|s`) % 9);

  const ms = Math.round((good ? 6000 : 14000) + pct(`${seed}|t`) * (good ? 22000 : 60000));
  const cost = +((good ? 0.03 : 0.09) + pct(`${seed}|c`) * (good ? 0.14 : 0.4)).toFixed(3);

  /* Tool CALLS, not just tool names. "Right tool, wrong arguments" is its own
     failure mode — Galileo scores tool selection and arguments together, and
     agentevals makes argument matching a dial orthogonal to trajectory matching.
     A degraded run keeps the tool and corrupts one argument, which is exactly
     the bug a name-only check waves through. */
  const toolCalls: EvalToolCall[] = tools.map((name, i) => ({
    name,
    args: argsFor(argSpec[name], good, `${seed}|a${i}`),
  }));

  return { output, tools, toolCalls, steps, ms, cost, seed, quality: q };
}

/* Corrupt exactly one argument, in a way a real agent plausibly would: wrong
   case, truncated, or the field omitted altogether. */
function argsFor(spec: string | undefined, good: boolean, seed: string): Record<string, unknown> {
  if (!spec) return {};
  let parsed;
  try {
    parsed = JSON.parse(spec);
  } catch {
    return {}; // unparseable fixture spec → no args, same as an absent one
  }
  if (good || parsed == null || typeof parsed !== "object") return parsed || {};
  const keys = Object.keys(parsed);
  if (!keys.length) return parsed;
  const k = keys[hashStr(seed) % keys.length];
  const mode = hashStr(`${seed}|m`) % 3;
  const out = { ...parsed };
  if (mode === 0) delete out[k];
  else if (mode === 1 && typeof out[k] === "string") out[k] = out[k].toLowerCase();
  else out[k] = typeof out[k] === "string" ? out[k].slice(0, Math.max(1, Math.floor(out[k].length / 2))) : out[k];
  return out;
}

/* One case × one config. Pure — the page owns any staging or animation. */
export function evaluateCase(kase: EvalCase, evals: EvalSuite, key: string): EvalResult {
  const checks = checksFor(evals, kase);
  const trace = synthesize(kase, checks, key);
  const results = checks.map((c) =>
    runCheck(c, {
      output: trace.output,
      expected: kase.expected,
      tools: trace.tools,
      toolCalls: trace.toolCalls,
      steps: trace.steps,
      ms: trace.ms,
      cost: trace.cost,
      seed: trace.seed,
      quality: trace.quality,
    })
  );
  const score = composite(results);
  return {
    caseId: kase.id,
    output: trace.output,
    tools: trace.tools,
    toolCalls: trace.toolCalls,
    steps: trace.steps,
    ms: trace.ms,
    cost: trace.cost,
    checks: results,
    score,
    // "error" is reserved for a case that produced no gradeable signal at all —
    // distinct from a case that ran fine and scored badly.
    status: score == null ? "error" : score >= 0.7 ? "pass" : "fail",
  };
}

/* A full eval run. Immutable once built: it pins the config snapshot AND the
   case membership, so a comparison months later is still apples to apples even
   after cases have been added or edited (Phoenix makes the same guarantee). */
export function runEval(agent: Agent, evals: EvalSuite, { label, caseIds }: RunEvalOptions = {}): EvalRun {
  const snapshot = configSnapshot(agent);
  const key = snapshotKey(snapshot);
  const cases = (evals.cases || []).filter((c) => !caseIds || caseIds.includes(c.id));
  const results = cases.map((c) => evaluateCase(c, evals, key));
  const score = runScore(results);

  return {
    id: `run-${key}-${cases.length}-${hashStr(cases.map((c) => c.id).join()).toString(36).slice(0, 4)}`,
    when: "just now",
    label: label || `${agent.version} · ${cases.length} case${cases.length === 1 ? "" : "s"}`,
    target: { version: agent.version, snapshot, key },
    caseIds: cases.map((c) => c.id),
    // The graders are pinned too, not just the agent config. Editing a check
    // later must not silently reprice every historical run — that turns judge
    // drift into something indistinguishable from a real regression.
    checks: cases.map((c) => ({ caseId: c.id, checks: checksFor(evals, c) })),
    results,
    score,
    passRate: cases.length ? results.filter((r) => r.status === "pass").length / cases.length : 0,
    cost: +results.reduce((n, r) => n + r.cost, 0).toFixed(3),
    ms: results.reduce((n, r) => n + r.ms, 0),
  };
}

/* Row-matched comparison. Matched by case id, like LangSmith — Braintrust
   matches by input value, which is only worth the complexity when you need to
   compare across two different datasets. We have one suite; ids are exact. */
export function compareRuns(run: EvalRun | null | undefined, base: EvalRun | null | undefined): EvalCompareRow[] {
  if (!run) return [];
  const byId = new Map((base?.results || []).map((r) => [r.caseId, r] as const));
  return run.results.map((r) => {
    const b = byId.get(r.caseId);
    return {
      ...r,
      base: b || null,
      delta: b && r.score != null && b.score != null ? +(r.score - b.score).toFixed(3) : null,
    };
  });
}
